import { useFormDraft, useConfirmLeave } from './UnsavedChanges';
import { useId, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, post } from '@yingbaba/api';
import type { Portfolio, PortfolioMedia, ServicePackage } from '@yingbaba/types';
import { caseCategories } from '@yingbaba/utils';
import {
  Button,
  Modal,
  Field,
  Select,
  ErrorNotice,
  Notice,
  ActionDialog,
  Image,
  IconButton,
  PortfolioShowcase,
  portfolioMediaURL,
  portfolioImages,
  useToast,
} from '@yingbaba/ui';
import { Plus, Upload, ArrowLeft, ArrowRight, Play } from '@yingbaba/icons';

function readVideoDuration(file: File): Promise<number> {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    const url = URL.createObjectURL(file);
    const timer = window.setTimeout(() => finish(0), 5000);
    function finish(duration: number) {
      window.clearTimeout(timer);
      video.onloadedmetadata = null;
      video.onerror = null;
      video.removeAttribute('src');
      video.load();
      URL.revokeObjectURL(url);
      resolve(Number.isFinite(duration) ? Math.round(duration) : 0);
    }
    video.preload = 'metadata';
    video.onloadedmetadata = () => finish(video.duration);
    video.onerror = () => finish(0);
    video.src = url;
  });
}

export function PortfolioEditor({
  item,
  packages,
}: {
  item?: Portfolio;
  packages: ServicePackage[];
}) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<'details' | 'media'>('details');
  const [draft, setDraft] = useState<Portfolio | undefined>(item);
  const [busy, setBusy] = useState('');
  const pending = useRef(false);
  const [error, setError] = useState<unknown>(null);
  const [rightsConfirmed, setRightsConfirmed] = useState(false);
  const query = useQueryClient();
  const toast = useToast();
  const formID = useId();
  const id = (name: string) => `${formID}-${name}`;
  const images = draft ? portfolioImages(draft) : [];
  const leave = useConfirmLeave();
  const detailsDraft = useFormDraft({
    enabled: open && step === 'details',
    busy: !!busy,
    save: persist,
    discard: () => setOpen(false),
  });
  const close = () => leave(() => setOpen(false));

  async function refresh(value = draft) {
    if (!value) return;
    const current = await api<Portfolio>(`/cases/${value.id}`);
    setDraft(current);
    await query.invalidateQueries({
      predicate: (entry) => String(entry.queryKey).includes('cases'),
    });
  }

  async function save(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await persist(event.currentTarget);
  }
  async function persist(form: HTMLFormElement): Promise<boolean> {
    if (pending.current) return false;
    pending.current = true;
    setBusy('正在保存草稿…');
    setError(null);
    try {
      const fields = Object.fromEntries(new FormData(form));
      const value = await api<Portfolio>(draft ? `/cases/${draft.id}` : '/cases', {
        method: draft ? 'PUT' : 'POST',
        body: JSON.stringify({ ...fields, status: 'draft', cover: draft?.cover || '' }),
      });
      // Saving the introduction returns the record without media enrichment.
      // Those files were already saved separately and remain part of this draft.
      setDraft({
        ...value,
        media: draft?.media || value.media || [],
        credits: draft?.credits || value.credits || [],
      });
      detailsDraft.clean();
      setStep('media');
      await query.invalidateQueries({
        predicate: (entry) => String(entry.queryKey).includes('cases'),
      });
      toast('草稿已保存，可以添加作品媒体');
      return true;
    } catch (reason) {
      setError(reason);
      return false;
    } finally {
      pending.current = false;
      setBusy('');
    }
  }

  async function upload(kind: PortfolioMedia['kind'], selected: File[]) {
    if (!draft || !selected.length || pending.current) return;
    setError(null);
    if (kind === 'image' && images.length + selected.length > 12) {
      setError(new Error('每个案例最多 12 张作品图片，请减少选择数量'));
      return;
    }
    const limit = (kind === 'video' ? 200 : 10) * 1024 * 1024;
    if (selected.some((file) => file.size > limit)) {
      setError(new Error(kind === 'video' ? '视频最大 200 MB' : '每张图片最大 10 MB'));
      return;
    }
    pending.current = true;
    setBusy('正在准备上传…');
    let uploaded = 0;
    try {
      for (const file of selected) {
        setBusy(`正在上传 ${uploaded + 1} / ${selected.length}：${file.name}`);
        const body = new FormData();
        body.append('kind', kind);
        body.append('file', file);
        if (kind === 'video')
          body.append('duration_seconds', String(await readVideoDuration(file)));
        await api(`/cases/${draft.id}/media`, { method: 'POST', body });
        uploaded++;
      }
      toast('作品文件已保存');
    } catch (reason) {
      const detail = reason instanceof Error ? reason.message : '上传失败，请重试';
      setError(
        new Error(uploaded ? `已保存 ${uploaded} 个文件，后续上传未完成：${detail}` : detail),
      );
    } finally {
      try {
        await refresh();
      } catch (reason) {
        setError(reason);
      }
      pending.current = false;
      setBusy('');
    }
  }

  async function reorder(index: number, direction: number) {
    if (!draft || pending.current) return;
    pending.current = true;
    setBusy('正在调整图片顺序…');
    setError(null);
    const ordered = images.map((file) => file.id);
    [ordered[index], ordered[index + direction]] = [ordered[index + direction], ordered[index]];
    try {
      await api(`/cases/${draft.id}/media/order`, {
        method: 'PUT',
        body: JSON.stringify({ ids: ordered }),
      });
      await refresh();
    } catch (reason) {
      setError(reason);
    } finally {
      pending.current = false;
      setBusy('');
    }
  }

  async function submitReview() {
    if (!draft || pending.current || !rightsConfirmed) return;
    pending.current = true;
    setBusy('正在提交审核…');
    setError(null);
    try {
      await post(`/cases/${draft.id}/submit`);
      await query.invalidateQueries();
      setOpen(false);
      toast('案例已提交审核，通过后公开展示');
    } catch (reason) {
      setError(reason);
    } finally {
      pending.current = false;
      setBusy('');
    }
  }

  function uploadField(kind: PortfolioMedia['kind'], label: string, hint: string) {
    return (
      <div className="portfolio-upload-control">
        <label className={`btn btn-secondary ${busy ? 'disabled' : ''}`} htmlFor={id(kind)}>
          <Upload size={15} /> {label}
        </label>
        <input
          type="file"
          id={id(kind)}
          aria-label={label}
          className="visually-hidden"
          disabled={Boolean(busy)}
          multiple={kind === 'image'}
          accept={
            kind === 'video'
              ? '.mp4,.m4v,video/mp4'
              : '.jpg,.jpeg,.png,.webp,.gif,image/jpeg,image/png,image/webp,image/gif'
          }
          onChange={(event) => {
            void upload(kind, Array.from(event.target.files || []));
            event.target.value = '';
          }}
        />
        <p className="muted small">{hint}</p>
      </div>
    );
  }
  const hasContent = Boolean(draft?.media?.length || draft?.video_url);

  return (
    <>
      <Button
        size="sm"
        variant={item ? 'secondary' : 'primary'}
        onClick={() => {
          setDraft(item);
          setStep('details');
          setError(null);
          setRightsConfirmed(false);
          setOpen(true);
        }}
      >
        {!item && <Plus size={15} />}
        {item ? (item.status === 'published' ? '编辑案例' : '编辑草稿与作品') : '添加案例'}
      </Button>
      <Modal
        open={open}
        onOpenChange={(value) => {
          if (!pending.current && !value) close();
        }}
        title={item ? '编辑作品案例' : '添加作品案例'}
        className="portfolio-editor-dialog"
        busy={!!busy}
        description="保存介绍后，可上传作品视频、封面和多张图片；审核通过后同步到网站和小程序。"
        footer={
          step === 'details' ? (
            <>
              <Button type="button" variant="secondary" disabled={!!busy} onClick={close}>
                取消
              </Button>
              <Button type="submit" form={id('form')} loading={!!busy}>
                保存并管理作品 <ArrowRight size={15} />
              </Button>
            </>
          ) : (
            draft && (
              <>
                <Button variant="secondary" disabled={!!busy} onClick={close}>
                  保留草稿
                </Button>
                <Button
                  loading={!!busy}
                  disabled={!hasContent || !rightsConfirmed}
                  onClick={() => void submitReview()}
                >
                  提交案例审核
                </Button>
              </>
            )
          )
        }
      >
        <ol className="portfolio-editor-steps" aria-label="案例编辑步骤">
          <li aria-current={step === 'details' ? 'step' : undefined}>
            <span>1</span>
            <div>
              <strong>作品介绍</strong>
              <small>填写背景与创作方案</small>
            </div>
          </li>
          <li aria-current={step === 'media' ? 'step' : undefined}>
            <span>2</span>
            <div>
              <strong>视频与图片</strong>
              <small>上传作品并提交审核</small>
            </div>
          </li>
        </ol>
        {step === 'details' ? (
          <form
            id={id('form')}
            className="form-stack portfolio-editor-form"
            ref={detailsDraft.ref}
            onChange={detailsDraft.onChange}
            onSubmit={save}
            key={draft?.id || 'new'}
          >
            <fieldset className="form-draft-fields" disabled={!!busy}>
              {item?.status === 'published' && (
                <Notice>保存后案例将暂时下线成为草稿，完善作品并重新审核通过后恢复展示。</Notice>
              )}
              <Field label="案例名称" htmlFor={id('title')}>
                <input
                  className="input"
                  id={id('title')}
                  name="title"
                  defaultValue={draft?.title}
                  required
                  minLength={2}
                  maxLength={100}
                />
              </Field>
              <div className="grid-2">
                <Field label="作品类型" htmlFor={id('category')}>
                  <Select
                    id={id('category')}
                    name="category"
                    defaultValue={draft?.category || caseCategories[0]}
                  >
                    {Array.from(
                      new Set([...(draft?.category ? [draft.category] : []), ...caseCategories]),
                    ).map((category) => (
                      <option key={category}>{category}</option>
                    ))}
                  </Select>
                </Field>
                <Field label="客户行业" htmlFor={id('industry')}>
                  <input
                    className="input"
                    id={id('industry')}
                    name="industry"
                    defaultValue={draft?.industry}
                    required
                    minLength={2}
                    maxLength={60}
                    placeholder="例如：消费品牌、城市文旅"
                  />
                </Field>
              </div>
              <Field label="风格 / 能力标签" htmlFor={id('tags')} hint="多个标签用逗号分隔">
                <input
                  className="input"
                  id={id('tags')}
                  name="tags"
                  defaultValue={draft?.tags}
                  maxLength={200}
                />
              </Field>
              <Field label="关联标准服务" htmlFor={id('package')}>
                <Select
                  id={id('package')}
                  name="service_package_id"
                  defaultValue={draft?.service_package_id || ''}
                >
                  <option value="">不关联标准服务</option>
                  {packages.map((pkg) => (
                    <option key={pkg.id} value={pkg.id}>
                      {pkg.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field
                label="创作背景与执行方案"
                htmlFor={id('description')}
                hint="介绍客户目标、创意方向和实际执行，建议写明自己参与的工作。"
              >
                <textarea
                  id={id('description')}
                  name="description"
                  defaultValue={draft?.description}
                  required
                  minLength={20}
                  maxLength={8000}
                  rows={5}
                />
              </Field>
              <Field
                label="外部作品链接（可选）"
                htmlFor={id('video_url')}
                hint="可填写 HTTPS 视频直链或视频平台页面；上传的作品视频优先展示。"
              >
                <input
                  className="input"
                  id={id('video_url')}
                  name="video_url"
                  type="url"
                  defaultValue={draft?.video_url}
                  placeholder="https://"
                />
              </Field>
              <ErrorNotice error={error} />
            </fieldset>
          </form>
        ) : (
          draft && (
            <div className="stack portfolio-editor">
              <div className="row spread wrap">
                <strong>{draft.title}</strong>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={Boolean(busy)}
                  onClick={() => setStep('details')}
                >
                  <ArrowLeft size={14} /> 编辑介绍
                </Button>
              </div>
              <section className="portfolio-editor-uploads">
                {uploadField(
                  'cover',
                  draft.media?.some((file) => file.kind === 'cover')
                    ? '更换作品封面'
                    : '上传作品封面',
                  '建议横版 16:9，图片最大 10 MB。',
                )}
                {uploadField(
                  'video',
                  draft.media?.some((file) => file.kind === 'video')
                    ? '更换作品视频'
                    : '上传作品视频',
                  'MP4，最大 200 MB；建议 H.264 视频 / AAC 音频。',
                )}
                {uploadField(
                  'image',
                  '添加作品图片',
                  `JPEG、PNG、WebP、GIF，每张最大 10 MB，可多选；已添加 ${images.length} / 12 张。`,
                )}
              </section>
              {busy && (
                <p className="notice" role="status">
                  {busy}，请保持窗口打开。
                </p>
              )}
              <ErrorNotice error={error} />
              <PortfolioShowcase
                key={`${draft.id}-${draft.updated_at}`}
                item={draft}
                scope="workspace"
              />
              {!!draft.media?.length && (
                <div className="portfolio-edit-files">
                  {draft.media.map((file) => {
                    const index = images.findIndex((entry) => entry.id === file.id);
                    return (
                      <div className="portfolio-edit-file" key={file.id}>
                        {file.kind === 'video' ? (
                          <Play size={24} />
                        ) : (
                          <Image
                            src={portfolioMediaURL(file, 'workspace')}
                            fallbackSrc={null}
                            alt=""
                          />
                        )}
                        <div className="grow">
                          <strong className="small">
                            {file.kind === 'cover'
                              ? '作品封面'
                              : file.kind === 'video'
                                ? '作品视频'
                                : `作品图片 ${index + 1}`}
                          </strong>
                          <p className="muted small">{file.name}</p>
                        </div>
                        <div className="row wrap">
                          {file.kind === 'image' && (
                            <>
                              <IconButton
                                label={`向前移动图片 ${index + 1}`}
                                disabled={Boolean(busy) || index === 0}
                                onClick={() => void reorder(index, -1)}
                              >
                                <ArrowLeft size={15} />
                              </IconButton>
                              <IconButton
                                label={`向后移动图片 ${index + 1}`}
                                disabled={Boolean(busy) || index === images.length - 1}
                                onClick={() => void reorder(index, 1)}
                              >
                                <ArrowRight size={15} />
                              </IconButton>
                            </>
                          )}
                          {!busy && (
                            <ActionDialog
                              title="移除作品文件"
                              trigger="移除"
                              variant="ghost"
                              intent="destructive"
                              submitLabel="确认移除"
                              method="DELETE"
                              path={`/cases/${draft.id}/media/${file.id}`}
                              description={`移除「${file.name}」后，请重新上传才能恢复。`}
                              onSuccess={() => void refresh().catch(setError)}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {!hasContent && (
                <Notice>
                  请添加作品视频、图片或外部作品链接，再提交审核。仅有参考配图的草稿可以先保存。
                </Notice>
              )}
              <label className="row portfolio-rights">
                <input
                  type="checkbox"
                  checked={rightsConfirmed}
                  onChange={(event) => setRightsConfirmed(event.target.checked)}
                />{' '}
                我确认拥有这些作品的展示权，并已获得客户及相关权利人的公开授权。
              </label>
            </div>
          )
        )}
      </Modal>
    </>
  );
}
